import { useState, useCallback } from 'react';
import { Col, Input, Label, Row, FormGroup, Spinner } from 'reactstrap';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../helpers/datePickerConfig';
import additioApiService from '../../../services/http/additioApiService';
import { getItem, STORAGE_KEYS } from '../../../services/storage';
import ModalClickable from '../../../layouts/modals/ModalClickable';

const MoodleToggle = ({ idAdditio, userOU }) => {
  const [moodleActive, setMoodleActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleToggle = useCallback(async () => {
    setLoading(true);
    try {
      const newState = !moodleActive;
      // Sede del alumno cuya ficha está abierta (misma fuente que el resto de la ficha)
      const headquarter = getItem(STORAGE_KEYS.STUDENT)?.empresaId;
      await additioApiService.cambiarEstadoAlumnoMoodle({
        id: idAdditio,
        isActive: newState,
        headquarter,
      });
      setMoodleActive(newState);
      setShowModal(true);
    } catch {
      // error handled by global handler
    } finally {
      setLoading(false);
    }
  }, [idAdditio, moodleActive]);

  if (!idAdditio) return null;

  return (
    <>
      {showModal && (
        <ModalClickable
          header={moodleActive ? 'Cuenta Moodle activada' : 'Cuenta Moodle desactivada'}
        />
      )}
      <FormGroup
        switch
        className="d-flex align-items-center gap-2"
        style={{ marginTop: '28px' }}
      >
        <Input
          type="switch"
          aria-label="Cuenta Moodle"
          checked={moodleActive}
          onChange={handleToggle}
          disabled={
            loading ||
            (userOU.centro !== 'SUPERADMIN' && userOU.centro !== 'SECRETARÍA')
          }
          style={{
            backgroundColor: moodleActive ? '#53e17a' : '',
            cursor: 'pointer',
          }}
        />
        <Label check style={{ margin: 0 }}>
          <small>
            {loading ? <Spinner size="sm" /> : moodleActive ? 'Cuenta Moodle activa' : 'Cuenta Moodle inactiva'}
          </small>
        </Label>
      </FormGroup>
    </>
  );
};

MoodleToggle.propTypes = {
  idAdditio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userOU: PropTypes.object.isRequired,
};

const SeccionOtros = ({ formik, fidelitationList, userOU }) => (
  <>
    <Row className="mt-2">
      <Col md="2">
        <Label htmlFor="conocer">
          <small>
            ¿Cómo nos has conocido?<span className="text-danger">*</span>
          </small>
        </Label>
        <Input
          id="conocer"
          name="conocer"
          type="select"
          onChange={formik.handleChange}
          value={formik.values.conocer}
          className={formik.errors.conocer ? 'border border-danger error' : ''}
          disabled={userOU.centro === 'ORIENTACIÓN'}
        >
          {fidelitationList !== ''
            ? fidelitationList.map((option) => (
                <option key={option.id} value={option.nom}>
                  {option.nom}
                </option>
              ))
            : ''}
        </Input>
        {formik.errors.conocer ? (
          <div className="text-danger tiny">{formik.errors.conocer}</div>
        ) : null}
      </Col>
      <Col md="2">
        <Label htmlFor="dndconocer">
          <small>¿Dónde nos has conocido?</small>
        </Label>
        <Input
          id="dndconocer"
          name="dndconocer"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.dndconocer}
          className={formik.errors.dndconocer ? 'border border-danger error' : ''}
          disabled={userOU.centro === 'ORIENTACIÓN'}
        />
      </Col>
      <Col md="2">
        <Label htmlFor="idAdditio">
          <small>ID Additio</small>
        </Label>
        <Input
          id="idAdditio"
          name="idAdditio"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.idAdditio}
          disabled={userOU.centro !== 'SUPERADMIN'}
        />
      </Col>
      <Col md="2">
        <MoodleToggle idAdditio={formik.values.idAdditio} userOU={userOU} />
      </Col>
      <Col md="2">
        <Label htmlFor="idStripe">
          <small>ID Stripe</small>
        </Label>
        <Input
          id="idStripe"
          name="idStripe"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.idStripe}
          disabled={userOU.centro !== 'SUPERADMIN'}
        />
      </Col>
    </Row>
    <Row className="mt-2">
      <Col md="2">
        <Label htmlFor="lopdFecha">
          <small>Fecha LOPD</small>
        </Label>
        <DatePicker
          selected={formik.values.lopdFecha}
          onChange={() => {}}
          dateFormat="dd/MM/yyyy"
          locale="es"
          disabled
          className="form-control"
          wrapperClassName="w-100"
        />
      </Col>
      <Col md="4">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '25px' }}>
          <Input
            type="checkbox"
            name="lopdSede"
            checked={formik.values.lopdSede}
            disabled={userOU.centro !== 'SUPERADMIN'}
            onChange={formik.handleChange}
            style={{
              display: 'block',
              height: '38px',
              width: '38px',
              margin: '0',
            }}
          />
          <Label htmlFor="lopdSede" style={{ margin: 0 }}>
            <small>Autoriza el acceso a notas a delegaciones</small>
          </Label>
        </div>
      </Col>
      <Col md="4">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '25px' }}>
          <Input
            type="checkbox"
            name="lopdComunicaciones"
            checked={formik.values.lopdComunicaciones}
            onChange={formik.handleChange}
            disabled={userOU.centro !== 'SUPERADMIN'}
            style={{
              display: 'block',
              height: '38px',
              width: '38px',
              margin: '0',
            }}
          />
          <Label htmlFor="lopdComunicaciones" style={{ margin: 0 }}>
            <small>Autoriza recibir comunicaciones de márketing</small>
          </Label>
        </div>
      </Col>
    </Row>
  </>
);

SeccionOtros.propTypes = {
  formik: PropTypes.object.isRequired,
  fidelitationList: PropTypes.array,
  userOU: PropTypes.object.isRequired,
};

export default SeccionOtros;
